import { offlineActiveRecordingIdAtom } from '@/atoms/offline-recording/sync';
import {
  isOfflineCaptureStopInFlight,
  OFFLINE_CAPTURE_STOP_TIMEOUT_MS,
  stopActiveOfflineCapture,
} from '@/hooks/desktop/offlineCaptureStop';
import {
  endActiveCaptureForExit,
  waitForManualCaptureStops,
} from '@/lib/desktopExitCaptureStop';
import { DOCUMENT_GENERATION_TYPES } from '@/lib/documentGenerationTypes';
import type { BackupRecoveryRow } from '@/tauri/controller/self-healing-stop';
import {
  SESSION_END_SWEEP_TIMEOUT_MS,
  sweepCaptureOnSessionEnd,
  type SessionEndSweepCallSite,
  type SessionEndSweepResult,
} from '@/tauri/online-chunk-upload/session-end-sweep';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { bridgeErrorKind, ipcInvokeRaw } from '@heidi/plugin-core';
import { type DocumentGenType } from '@heidi/schema/ml-scribe';
import {
  GET_DOCUMENT_BY_ID,
  StreamingStatus,
  type QueryDocumentResponse,
} from '@heidi/session/contracts';
import {
  getDocumentsBySessionId,
  waitForPendingDocumentRequests,
  waitForPendingNoteRequests,
} from '@heidi/session/data';
import { getDefaultStore } from 'jotai';

const DEADLINE_REACHED = Symbol('deadline-reached');
const MANDATORY_STATUS_READ_CEILING_MS = 5_000;

async function waitUntilDeadline<T>(
  promise: Promise<T>,
  deadlineAt: number
): Promise<T | typeof DEADLINE_REACHED> {
  const remainingMs = Math.max(0, deadlineAt - Date.now());
  if (remainingMs === 0) {
    void promise.catch(() => undefined);
    return DEADLINE_REACHED;
  }

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<typeof DEADLINE_REACHED>((resolve) => {
        timeoutId = setTimeout(() => resolve(DEADLINE_REACHED), remainingMs);
      }),
    ]);
  } finally {
    clearTimeout(timeoutId);
  }
}

function remainingBudget(deadlineAt: number): number {
  return Math.max(0, deadlineAt - Date.now());
}

// Keep the raw bridge here to avoid the controller import cycle through authActions.
async function onlineBackupRecoveryStatusQuietly(): Promise<BackupRecoveryRow[]> {
  if (!isTauri()) return [];
  const result = await ipcInvokeRaw('online_backup_recovery_status', {});
  return result.data as BackupRecoveryRow[];
}

function isUnconfirmedSweep(result: SessionEndSweepResult): boolean {
  if (!result.attempted) {
    return result.reason !== 'noActiveUpload';
  }
  return result.outcome === 'notConfirmed' || result.outcome === 'error';
}

type BackupWaitResult = {
  outcome: 'settled' | 'timedOut';
  deferred: boolean;
  waitedOnUnrelatedUpload: boolean;
};

function isDeferredBackupRow(row: BackupRecoveryRow): boolean {
  return row.status === 'pending' && row.lastErrorCode === 'AUTH_UNAVAILABLE';
}

async function waitForBackupUpload({
  sessionId,
  recordingId,
  deadlineAt,
  callSite,
  expectMatchingBackup,
  onPoll,
  onObservedRow,
}: {
  sessionId: string | null;
  recordingId: string | undefined;
  deadlineAt: number;
  callSite: SessionEndSweepCallSite;
  expectMatchingBackup: boolean;
  onPoll: () => void;
  onObservedRow: (row: BackupRecoveryRow) => void;
}): Promise<BackupWaitResult> {
  let polledAtLeastOnce = false;
  let waitedOnUnrelatedUpload = false;
  while (!polledAtLeastOnce || remainingBudget(deadlineAt) > 0) {
    try {
      const mandatoryRead = !polledAtLeastOnce;
      onPoll();
      polledAtLeastOnce = true;
      const statusRead = onlineBackupRecoveryStatusQuietly();
      const statusReadDeadlineAt =
        mandatoryRead && remainingBudget(deadlineAt) === 0
          ? Date.now() + MANDATORY_STATUS_READ_CEILING_MS
          : deadlineAt;
      const rows = await waitUntilDeadline(statusRead, statusReadDeadlineAt);
      if (rows === DEADLINE_REACHED) {
        return {
          outcome: 'timedOut',
          deferred: false,
          waitedOnUnrelatedUpload,
        };
      }

      const matchingRow =
        recordingId !== undefined
          ? rows.find(
              (row) =>
                row.recordingId === recordingId &&
                (sessionId === null || row.sessionId === sessionId)
            )
          : undefined;
      const anyUploading = rows.some((row) => row.status === 'uploading');
      if (matchingRow) {
        onObservedRow(matchingRow);
      }
      const matchingDeferred =
        matchingRow !== undefined && isDeferredBackupRow(matchingRow);
      if (matchingDeferred && !anyUploading) {
        return {
          outcome: 'settled',
          deferred: true,
          waitedOnUnrelatedUpload,
        };
      }
      const matchingInFlight =
        !matchingDeferred &&
        (matchingRow?.status === 'pending' || matchingRow?.status === 'uploading');
      const expectedMatchingRowMissing = expectMatchingBackup && !matchingRow;
      if (anyUploading && !matchingInFlight && !expectedMatchingRowMissing) {
        waitedOnUnrelatedUpload = true;
      }
      const shouldWait = anyUploading || expectedMatchingRowMissing || matchingInFlight;
      if (!shouldWait) {
        return {
          outcome: 'settled',
          deferred: false,
          waitedOnUnrelatedUpload,
        };
      }
    } catch (error) {
      logger.warn('DESKTOP_LOGOUT_BACKUP_STATUS_READ_FAILED', {
        callSite,
        errorKind: bridgeErrorKind(error),
      });
    }

    const delayMs = Math.min(1_000, remainingBudget(deadlineAt));
    if (delayMs === 0) break;
    const delay = new Promise<void>((resolve) => setTimeout(resolve, delayMs));
    if ((await waitUntilDeadline(delay, deadlineAt)) === DEADLINE_REACHED) {
      break;
    }
  }

  return {
    outcome: 'timedOut',
    deferred: false,
    waitedOnUnrelatedUpload,
  };
}

let settlementInFlight: Promise<'settled' | 'timedOut'> | null = null;

export function settleDesktopCaptureForLogout(
  callSite: 'logout' | 'logoutWithoutRedirect' | 'closeApp'
): Promise<'settled' | 'timedOut'> {
  if (settlementInFlight) {
    logger.info('DESKTOP_LOGOUT_CAPTURE_SETTLEMENT_JOINED', { callSite });
    return settlementInFlight;
  }

  const settlement = runDesktopCaptureSettlement(callSite);
  settlementInFlight = settlement;
  void settlement.then(
    () => {
      if (settlementInFlight === settlement) settlementInFlight = null;
    },
    () => {
      if (settlementInFlight === settlement) settlementInFlight = null;
    }
  );
  return settlement;
}

async function runDesktopCaptureSettlement(
  callSite: 'logout' | 'logoutWithoutRedirect' | 'closeApp'
): Promise<'settled' | 'timedOut'> {
  const startedAt = Date.now();
  const deadlineAt = startedAt + OFFLINE_CAPTURE_STOP_TIMEOUT_MS;
  const exitStop = await endActiveCaptureForExit(deadlineAt, callSite);
  const manualStop = await waitForManualCaptureStops(deadlineAt);
  let hadOnlineStop = false;
  let hadOfflineStop = false;
  let polledBackup = false;
  let backupStatus: BackupRecoveryRow['status'] | 'none' = 'none';
  let backupErrorCode: BackupRecoveryRow['lastErrorCode'] = null;
  let deferred = false;
  let waitedOnUnrelatedUpload = false;
  let noteRequest: 'notWaited' | 'none' | 'accepted' | 'ended' | 'timedOut' = 'notWaited';
  let documentRequest: 'notWaited' | 'none' | 'accepted' | 'ended' | 'timedOut' =
    'notWaited';
  let firstStopFailure: 'onlineStop' | 'offlineStop' | 'offlineStopFailed' | undefined;
  const waitedMs = () => Math.max(0, Date.now() - startedAt);
  const rememberStopFailure = (
    reason: 'onlineStop' | 'offlineStop' | 'offlineStopFailed'
  ) => {
    firstStopFailure ??= reason;
  };
  const logSettled = () => {
    logger.info('DESKTOP_LOGOUT_CAPTURE_SETTLED', {
      callSite,
      exitStop,
      waitedMs: waitedMs(),
      hadOnlineStop,
      hadOfflineStop,
      polledBackup,
      backupStatus,
      backupErrorCode,
      deferred,
      waitedOnUnrelatedUpload,
      manualStop,
      noteRequest,
      documentRequest,
    });
  };
  const logTimedOut = (
    reason:
      | 'offlineStop'
      | 'offlineStopFailed'
      | 'onlineStop'
      | 'manualStop'
      | 'backupUpload'
      | 'noteRequest'
      | 'documentRequest'
  ) => {
    logger.warn('DESKTOP_LOGOUT_CAPTURE_SETTLEMENT_TIMED_OUT', {
      callSite,
      exitStop,
      reason,
      manualStop,
      backupStatus,
      backupErrorCode,
      waitedOnUnrelatedUpload,
      waitedMs: waitedMs(),
      noteRequest,
      documentRequest,
    });
  };

  try {
    const offlineCaptureId = getDefaultStore().get(offlineActiveRecordingIdAtom);
    hadOfflineStop = offlineCaptureId !== null;
    const offlineStopInFlight =
      offlineCaptureId !== null && isOfflineCaptureStopInFlight(offlineCaptureId);
    const stopInFlight = manualStop === 'timedOut' || exitStop === 'timedOut';
    const stopCeilingMs = stopInFlight
      ? Math.max(0, Math.min(OFFLINE_CAPTURE_STOP_TIMEOUT_MS, deadlineAt - Date.now()))
      : OFFLINE_CAPTURE_STOP_TIMEOUT_MS;
    const sweepPromise = sweepCaptureOnSessionEnd({
      target: { kind: 'anyActiveUpload' },
      callSite,
      ...(stopInFlight ? { stopInFlight: true } : {}),
      boundMs: SESSION_END_SWEEP_TIMEOUT_MS,
      stopCeilingMs,
    })
      .then((sweep) => {
        if (isUnconfirmedSweep(sweep)) rememberStopFailure('onlineStop');
        return sweep;
      })
      .catch((error) => {
        logger.error('DESKTOP_LOGOUT_CAPTURE_SETTLEMENT_FAILED', {
          callSite,
          errorKind: bridgeErrorKind(error),
        });
        rememberStopFailure('onlineStop');
        return {
          attempted: true,
          basis: 'unprobed',
          outcome: 'error',
        } satisfies SessionEndSweepResult;
      });
    const offlineStopPromise = offlineCaptureId
      ? (offlineStopInFlight
          ? Promise.resolve('timedOut' as const)
          : stopActiveOfflineCapture(
              offlineCaptureId,
              callSite === 'closeApp' ? 'closeGuard' : 'logout',
              stopInFlight ? stopCeilingMs : OFFLINE_CAPTURE_STOP_TIMEOUT_MS
            ).catch((error) => {
              logger.error('DESKTOP_LOGOUT_CAPTURE_SETTLEMENT_FAILED', {
                callSite,
                errorKind: bridgeErrorKind(error),
              });
              return 'failed' as const;
            })
        ).then((offlineStop) => {
          if (offlineStop === 'timedOut') rememberStopFailure('offlineStop');
          if (offlineStop === 'failed') {
            rememberStopFailure('offlineStopFailed');
          }
          return offlineStop;
        })
      : Promise.resolve<'settled' | 'timedOut' | 'failed'>('settled');
    const [sweep] = await Promise.all([sweepPromise, offlineStopPromise]);

    hadOnlineStop = sweep.attempted && sweep.outcome === 'stopped';
    const stoppedSweep =
      sweep.attempted && sweep.outcome === 'stopped' ? sweep : undefined;
    const recordingId = stoppedSweep?.recordingId;
    const expectMatchingBackup =
      stoppedSweep?.backupPendingAsync === true && recordingId !== undefined;

    const backupResult = await waitForBackupUpload({
      sessionId: stoppedSweep?.sessionId ?? null,
      recordingId,
      deadlineAt,
      callSite,
      expectMatchingBackup,
      onPoll: () => {
        polledBackup = true;
      },
      onObservedRow: (row) => {
        backupStatus = row.status;
        backupErrorCode = row.lastErrorCode;
      },
    });
    waitedOnUnrelatedUpload = backupResult.waitedOnUnrelatedUpload;
    noteRequest = await waitForPendingNoteRequests(deadlineAt);
    documentRequest = await waitForPendingDocumentRequests(
      deadlineAt,
      async (sessionId) => {
        try {
          const documentsResponse = await waitUntilDeadline(
            getDocumentsBySessionId(sessionId),
            deadlineAt
          );
          if (documentsResponse === DEADLINE_REACHED) return true;
          return documentsResponse.data.documents.some((document) => {
            if (document.content && document.content.trim() !== '') return false;
            const cachedDocument = reactQueryClient.getQueryData<QueryDocumentResponse>([
              GET_DOCUMENT_BY_ID,
              { sessionId, documentId: document.id },
            ]);
            return (
              cachedDocument?.streamingStatus !== StreamingStatus.MANUALLY_ABORTED &&
              DOCUMENT_GENERATION_TYPES.has(document.generation_type as DocumentGenType)
            );
          });
        } catch {
          return false;
        }
      }
    );
    const timeoutReason =
      firstStopFailure ??
      (manualStop === 'timedOut' ? 'manualStop' : undefined) ??
      (backupResult.outcome === 'timedOut' ? 'backupUpload' : undefined) ??
      (noteRequest === 'timedOut' ? 'noteRequest' : undefined) ??
      (documentRequest === 'timedOut' ? 'documentRequest' : undefined);
    if (timeoutReason) {
      logTimedOut(timeoutReason);
      return 'timedOut';
    }
    deferred = backupResult.deferred;

    logSettled();
    return 'settled';
  } catch (error) {
    logger.error('DESKTOP_LOGOUT_CAPTURE_SETTLEMENT_FAILED', {
      callSite,
      errorKind: bridgeErrorKind(error),
    });
    logTimedOut(firstStopFailure ?? 'backupUpload');
    return 'timedOut';
  }
}
